/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @link      https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments 2026.
 * @license   MIT
 * @description Frontend component for managing the player's trap, including scouting and selling products.
 */

import React, { useState } from "react";
import {
  Users,
  Star,
  Shield,
  FlaskConical,
  Search,
  Clock,
  ChevronRight,
} from "lucide-react";
import GirlsManagement from "./GirlsManagement";
import RunnersManagement from "./RunnersManagement";
import CooksManagement from "./CooksManagement";
import ThugsManagement from "./ThugsManagement";

const PersonnelCard = ({
  title,
  count,
  type,
  icon: Icon,
  color,
  turns,
  onAction,
  scoutAmount,
  onAmountChange,
  onManageClick,
  topRight,
}) => {
  const colorMap = {
    green: {
      text: "text-green-500",
      border: "hover:border-green-900/40",
      bg: "bg-green-600",
      hover: "hover:bg-green-500",
    },
    red: {
      text: "text-red-500",
      border: "hover:border-red-900/40",
      bg: "bg-red-600",
      hover: "hover:bg-red-500",
    },
    blue: {
      text: "text-blue-500",
      border: "hover:border-blue-900/40",
      bg: "bg-blue-600",
      hover: "hover:bg-blue-500",
    },
  };
  const theme = colorMap[color] || colorMap.green;

  return (
    <div
      className={`bg-zinc-950 border-2 border-zinc-900 ${theme.border} transition-all p-5 flex flex-col justify-between group relative overflow-hidden`}
    >
      <div
        className={`absolute -right-4 -top-4 opacity-5 group-hover:opacity-10 transition-opacity ${theme.text}`}
      >
        <Icon size={120} />
      </div>

      <div className="flex justify-between items-start mb-6 relative z-10">
        <div>
          <p
            className={`text-[10px] ${theme.text} font-black uppercase tracking-[0.2em] flex items-center gap-2`}
          >
            <Icon size={14} /> {title}
          </p>
          <p className="text-4xl font-black text-white italic mt-1 font-mono">
            {count}
          </p>
        </div>

        {/* ADDED: Dynamic Top-Right Data */}
        {topRight && (
          <div className="text-right">
            <p className="text-[8px] text-zinc-600 uppercase font-black">
              {topRight.label}
            </p>
            <p
              className={`text-xs font-black ${topRight.color || "text-white"}`}
            >
              {topRight.value}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-3 pt-5 border-t border-zinc-900/50 relative z-10">
        <div className="flex items-center gap-2">
          <div className="relative w-20">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[7px] font-black text-zinc-600 uppercase">
              Qty
            </span>
            <input
              type="number"
              value={scoutAmount}
              onChange={(e) => onAmountChange(e.target.value)}
              className="w-full bg-black border border-zinc-800 p-2 pl-7 text-xs font-mono text-white outline-none focus:border-zinc-700 transition-colors"
            />
          </div>
          <button
            onClick={() => onAction(scoutAmount)}
            className={`flex-1 ${theme.bg} ${theme.hover} text-black font-black text-[10px] py-2.5 uppercase italic flex items-center justify-center gap-2 transition-all active:scale-95`}
          >
            <Search size={12} strokeWidth={3} /> Scout Personnel
          </button>
          <button
            onClick={onManageClick}
            className="p-2.5 bg-zinc-900 text-zinc-600 hover:text-white hover:bg-zinc-800 transition-all border border-zinc-800"
          >
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="flex justify-between items-center text-[8px] text-zinc-600 uppercase font-bold tracking-tighter">
          <span>
            <Clock size={10} className="inline mr-1" /> {turns} Turns / Unit
          </span>
          <span className="opacity-0 group-hover:opacity-100 transition-opacity">
            Requesting {scoutAmount * turns} Turns
          </span>
        </div>
      </div>
    </div>
  );
};

export default function TrapManagement({
  stats,
  onScout,
  onScoutGirls,
  onScoutThugs,
  onScoutCooks,
  onEquip,
  fetchStats,
}) {
  const [currentView, setCurrentView] = useState("overview");
  const [scoutAmounts, setScoutAmounts] = useState({
    runners: 1,
    girls: 1,
    thugs: 1,
    cooks: 1,
  });

  const handleAmountChange = (type, val) => {
    setScoutAmounts((prev) => ({
      ...prev,
      [type]: Math.max(1, parseInt(val) || 1),
    }));
  };

  // --- STAT CALCULATIONS FOR TOP RIGHT DISPLAY ---
  const runnersTotalRate =
    stats.inventory
      ?.filter((i) => i.type === "runner")
      .reduce((acc, i) => acc + (i.production_rate || 0) * i.quantity, 0) || 0;

  const cooksTotalRate =
    stats.inventory
      ?.filter((i) => i.type === "cook")
      .reduce((acc, i) => acc + (i.production_rate || 0) * i.quantity, 0) || 0;

  const totalAtk =
    stats.inventory
      ?.filter((i) => i.type === "mercenary")
      .reduce((acc, t) => acc + (t.atk_power || 0) * t.quantity, 0) || 0;

  const totalDef =
    stats.inventory
      ?.filter((i) => i.type === "mercenary")
      .reduce((acc, t) => acc + (t.def_power || 0) * t.quantity, 0) || 0;

  const cooksCount =
    stats.inventory
      ?.filter((i) => i.type === "cook")
      .reduce((s, i) => s + i.quantity, 0) || 0;

  // Navigation Logic
  if (currentView !== "overview") {
    const ViewMap = {
      girls: GirlsManagement,
      runners: RunnersManagement,
      cooks: CooksManagement,
      thugs: ThugsManagement,
    };
    const ActiveView = ViewMap[currentView];
    return (
      <ActiveView
        inventory={stats.inventory}
        happiness={stats.girls_happiness}
        onEquip={onEquip}
        fetchStats={fetchStats}
        onBack={() => setCurrentView("overview")}
      />
    );
  }

  return (
    <div className="space-y-6 animate-in slide-in-from-left fade-in duration-700 font-mono">
      <div className="flex items-end justify-between border-b-2 border-zinc-900 pb-5">
        <div>
          <h2 className="text-4xl font-black italic text-white uppercase leading-none">
            The Crib
          </h2>
          <p className="text-zinc-600 text-[9px] uppercase tracking-[0.4em] mt-2">
            Empire Base & Personnel Logistics
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PersonnelCard
          title="Street Runners"
          count={stats.runners}
          type="runners"
          icon={Users}
          color="green"
          turns={2}
          onAction={onScout}
          scoutAmount={scoutAmounts.runners}
          onAmountChange={(val) => handleAmountChange("runners", val)}
          onManageClick={() => setCurrentView("runners")}
          topRight={{
            label: "Sold / Tick",
            value: `~${Math.round(runnersTotalRate * 100)}%`,
            color: "text-blue-500",
          }}
        />
        <PersonnelCard
          title="Active Muscle"
          count={stats.thugs}
          type="thugs"
          icon={Shield}
          color="red"
          turns={3}
          onAction={onScoutThugs}
          scoutAmount={scoutAmounts.thugs}
          onAmountChange={(val) => handleAmountChange("thugs", val)}
          onManageClick={() => setCurrentView("thugs")}
          topRight={{
            label: "Atk / Def",
            value: `${totalAtk.toLocaleString()} / ${totalDef.toLocaleString()}`,
            color: "text-red-500",
          }}
        />
        <PersonnelCard
          title="Lab Cooks"
          count={cooksCount}
          type="cooks"
          icon={FlaskConical}
          color="blue"
          turns={5}
          onAction={onScoutCooks}
          scoutAmount={scoutAmounts.cooks}
          onAmountChange={(val) => handleAmountChange("cooks", val)}
          onManageClick={() => setCurrentView("cooks")}
          topRight={{
            label: "Cooked / Tick",
            value: `${Math.floor(cooksTotalRate)} u`,
            color: "text-blue-500",
          }}
        />

        {/* Girls/Hos Card with Happiness bar integrated */}
        <div className="bg-zinc-950 border-2 border-zinc-900 hover:border-pink-900/40 transition-all p-5 flex flex-col justify-between group relative overflow-hidden">
          <div className="absolute -right-4 -top-4 opacity-5 group-hover:opacity-10 transition-opacity text-pink-500">
            <Star size={120} />
          </div>

          <div className="flex justify-between items-start mb-2 relative z-10">
            <div>
              <p className="text-[10px] text-pink-500 font-black uppercase tracking-[0.2em] flex items-center gap-2">
                <Star size={14} /> Prostitutes
              </p>
              <p className="text-4xl font-black text-white italic mt-1 font-mono">
                {stats.girls || 0}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[8px] text-zinc-600 uppercase font-black">
                Happiness
              </p>
              <p
                className={`text-xs font-black ${stats.girls_happiness > 70 ? "text-green-500" : "text-yellow-500"}`}
              >
                {stats.girls_happiness}%
              </p>
            </div>
          </div>

          <div className="w-full h-1 bg-zinc-900 mb-6 relative z-10">
            <div
              className="h-full bg-pink-600 transition-all duration-1000"
              style={{ width: `${stats.girls_happiness}%` }}
            />
          </div>

          <div className="space-y-3 pt-5 border-t border-zinc-900/50 relative z-10">
            <div className="flex items-center gap-2">
              <div className="relative w-20">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[7px] font-black text-zinc-600 uppercase">
                  Qty
                </span>
                <input
                  type="number"
                  value={scoutAmounts.girls}
                  onChange={(e) => handleAmountChange("girls", e.target.value)}
                  className="w-full bg-black border border-zinc-800 p-2 pl-7 text-xs font-mono text-white outline-none focus:border-zinc-700 transition-colors"
                />
              </div>
              <button
                onClick={() => onScoutGirls(scoutAmounts.girls)}
                className="flex-1 bg-pink-600 hover:bg-pink-500 text-black font-black text-[10px] py-2.5 uppercase italic flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                <Search size={12} strokeWidth={3} /> Scout Personnel
              </button>
              <button
                onClick={() => setCurrentView("girls")}
                className="p-2.5 bg-zinc-900 text-zinc-600 hover:text-white hover:bg-zinc-800 transition-all border border-zinc-800"
              >
                <ChevronRight size={18} />
              </button>
            </div>
            <div className="text-[8px] text-zinc-600 uppercase font-bold flex items-center gap-1">
              <Clock size={10} /> 2 Turns / Unit
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
